import React from 'react'
import { Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import {
  ButtonActionsHL,
  ButtonHLPure,
  CheckBoxHLPure,
  DialogFormHLPure,
  DialogPdfHLPure,
  DialogPdf,
  DialogSystemGLPure,
  GridSelectionHLPure,
  InputFileHLPure,
  InputHLPure,
  SearchBarHLPure,
  TextAreaHLPure,
} from '../../pures'
import './document-panel.view.css'

/**
 * @author Edgar Escobedo
 * @description DocumentPanelView displays buttons for additional actions for documents in digital vault.
 * @typedef {Object} Props
 * @property {string} blobUrl - The Blob URL for the selected PDF, used for donlowads or previews.
 * @property {Object} documentData - The document data object containing metadata and info about the document.
 * @property {string} email - The email address to send the document to.
 * @property {boolean} errorFile - Flag to display an error message if there is an error with the file.
 * @property {string} errorInput - Flag to display an error message if there is an error with the email input.
 * @property {boolean} errorInputArea - Flag indicating if there is an error in the message textarea.
 * @property {string} errorInputText - Error message for the email input.
 * @property {string} errorTextArea - Error message for the message textarea.
 * @property {Object} fileExpSelected - The selected file from an expediente (case file), if applicable.
 * @property {number} heightBody - The height of the body for the dialog.
 * @property {number} heightContainer - The height of the container for the dialog.
 * @property {Object} handlers - Object containing functions to handle various actions.
 * @property {string} messageEmail - The message to be sent with the email.
 * @property {string} messageShare - The message to be sent when sharing the document.
 * @property {Array<{ name: string, value: boolean, nameBD: string, icon: string, checked: boolean }>} permisos - Array of permissions to display buttons for actions.
 * @property {{ alerts: boolean, edit: boolean, print: boolean, download: boolean, send: boolean, share: boolean, delete: boolean, version: boolean }} permissions - Object containing permissions for actions.
 * @property {React.ReactNode} pdf - The rendered PDF reader component.
 * @property {boolean} selectedFile - Flag indicating if a file has been selected.
 * @property {string} selectedFileBase64 - Base64 string of the selected file.
 * @property {string} selectedFileName - Name of the selected file.
 * @property {function} setShowDialogVersion - Function to set the visibility of the version dialog.
 * @property {boolean} showDeleteDocument - Flag to show the delete document dialog.
 * @property {boolean} showDialogPrint - Flag to show the print dialog.
 * @property {boolean} showDialogSendEmail - Flag to show the send email dialog.
 * @property {boolean} showDialogShare - Flag to show the share dialog.
 * @property {boolean} showDialogVersion - Flag to show the version dialog.
 * @property {boolean} signedInGovo - Flag indicating if the user is signed in to Govo.
 * @property {string} userSearch - The search term for filtering users in the share dialog.
 * @property {Object} userSendGridSelected - The selected user from the send grid.
 * @property {Array} usersGrid - Array of users to display in the share dialog.
 * @property {Array} usersSendGrid - Array of users to display in the send grid.
 * @property {number} widthContainer - The width of the container for the dialog.
 * @property {boolean} showLeftDownload - Flag to show the download button on the left side.
 * @property {boolean} showShareLeft - Flag to show the share button on the left side.
 * @property {boolean} showDeleteLeft - Flag to show the delete button on the left side.
 * @param {Props} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const DocumentPanelView = ({
  blobUrl,
  documentData,
  email,
  errorFile,
  errorInput,
  errorInputArea,
  errorInputText,
  errorTextArea,
  fileExpSelected,
  handlers,
  heightBody,
  heightContainer,
  messageEmail,
  messageShare,
  permisos,
  permissions,
  pdf,
  selectedFile,
  selectedFileBase64,
  selectedFileName,
  setShowDialogVersion,
  showDeleteDocument,
  showDialogPrint,
  showDialogSendEmail,
  showDialogShare,
  showDialogVersion,
  signedInGovo,
  userSearch,
  userSendGridSelected,
  usersGrid,
  usersSendGrid,
  widthContainer,
  fromDelete,
  onClickBtnActions,
  isEditing,
  onToggleEdit,
  onRequestSave,
  showLeftDownload,
  showShareLeft,
  showDeleteLeft,
  showEditLeft
}) => {
  const { t } = useTranslation()

  const currentPath = window.location.hash || window.location.pathname
  const isInBinPath = currentPath.includes('papelera') ||
    currentPath.includes('bin') ||
    currentPath.includes('/digital-vault/bin')

  const infoTypeModal = (documentData && documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_EXPEDIENTE) ? t('digitalVaultUploadView.data.exp.minus') : t('digitalVaultUploadView.data.document.minus');

  return (
    <div
      className='documentPanelActions-container'
      style={{ overflowX: 'auto', overflowY: 'hidden' }}
    >
      {(() => {
        const hasLeftGroup = showLeftDownload || showShareLeft || showDeleteLeft
        if (!hasLeftGroup) return null
        return (
          <div className='documentPanelActions-left-group'>
            {
              isEditing && showEditLeft && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={() => onToggleEdit()}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_REJECT}`}
                    height='30'
                    width='30'
                    alt={t('button.cancel')}
                  />
                  <h1>{t('button.cancel')}</h1>
                </div>
              )
            }
            {(permissions.edit && showEditLeft) && (
              <div
                className='documentPanelActions-container-btns'
                onClick={async () => {
                  if (isEditing) {
                    const ok = await (typeof onRequestSave === 'function' ? onRequestSave() : Promise.resolve(false))
                    if (ok && typeof onToggleEdit === 'function') { onToggleEdit() }
                  } else {
                    if (typeof onToggleEdit === 'function') { onToggleEdit() }
                  }
                }}
              >
                <input
                  type='image'
                  src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${isEditing ? process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SAVE : process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_EDIT}`}
                  height='30'
                  width='30'
                  alt={isEditing ? t('button.save') : t('documentPanelActions.edit')}
                />
                <h1>{isEditing ? t('button.save') : t('documentPanelActions.edit')}</h1>
              </div>
            )}
            {!isEditing && (
              <>
                {showLeftDownload && (
                  <div
                    className='documentPanelActions-container-btns'
                    onClick={() => handlers.handleDownloadDoc()}
                  >
                    <input
                      type='image'
                      src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_DOWNLOAD_EXPEDIENTE}`}
                      height='30'
                      width='30'
                      alt={t('documentPanelActions.download')}
                    />
                    <h1>{t('documentPanelActions.download')}</h1>
                  </div>
                )}
                {showShareLeft && (
                  <div
                    className='documentPanelActions-container-btns'
                    onClick={(e) => handlers.handleOpenCloseDialogShare(e)}
                  >
                    <input
                      type='image'
                      src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SHARE}`}
                      height='30'
                      width='30'
                      alt={t('documentPanelActions.share')}
                    />
                    <h1>{t('documentPanelActions.share')}</h1>
                  </div>

                )}
                {showDeleteLeft && (
                  <div
                    className='documentPanelActions-container-btns'
                    onClick={(e) => handlers.handleShowDialogDelete(e)}
                  >
                    <input
                      type='image'
                      src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_TRASH}`}
                      height='30'
                      width='30'
                      alt={t('documentPanelActions.delete')}
                    />
                    <h1>{t('documentPanelActions.delete')}</h1>
                  </div>
                )}
              </>
            )}
          </div>
        )
      })()}

      <div className='documentPanelActions-right-group'>
        {(() => {
          const shouldShowBinActions = !fromDelete && ((window.location.hash || window.location.pathname).includes('papelera') ||
            (window.location.hash || window.location.pathname).includes('bin') ||
            (window.location.hash || window.location.pathname).includes('/digital-vault/bin'))

          return shouldShowBinActions && permisos && permisos.map((accion, index) => (
            <div
              key={`bin-${index}`}
              className='documentPanelActions-container-btns'
              onClick={(e) => onClickBtnActions && onClickBtnActions(e, accion)}
            >
              <input
                type='image'
                src={accion.icon}
                height='30'
                width='30'
                alt={accion.name}
              />
              <h1>{accion.name}</h1>
            </div>
          ));
        })()}

        {!isEditing && <>
          {permissions.print && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleShowDialogPrint(e, true)}
            >
              <input
                type='image'
                src={`${(documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_DOCUMENT) ? process.env.REACT_APP_AWS_S3_BUCKET_URL + process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_PRINT : process.env.REACT_APP_AWS_S3_BUCKET_URL + process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_PRINT_DOCUMENT}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.print')}
              />
              <h1>{t('documentPanelActions.print')}</h1>
            </div>
          )}
          {permissions.download && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={() => handlers.handleDownloadDoc(true)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_DOWNLOAD}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.download')}
              />
              <h1>{t('documentPanelActions.download')}</h1>
            </div>
          )}
          {permissions.send && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleShowDialog(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SEND}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.send')}
              />
              <h1>{t('documentPanelActions.send')}</h1>
            </div>
          )}
          {permissions.share && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleOpenCloseDialogShare(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SHARE}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.share')}
              />
              <h1>{t('documentPanelActions.share')}</h1>
            </div>
          )}
          {(permissions.delete && !isEditing) && !isInBinPath && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleShowDialogDelete(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_TRASH}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.delete')}
              />
              <h1>{t('documentPanelActions.delete')}</h1>
            </div>
          )}
          {(documentData.infoType === 'Documento' && !signedInGovo && !isEditing) && !isInBinPath && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleSignRequest(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SIGN_REQUEST}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.signRequest')}
              />
              <h1>{t('documentPanelActions.signRequest')}</h1>
            </div>
          )}
        </>
        }
        {
          isEditing && !showEditLeft && (
            <div
              className='documentPanelActions-container-btns'
              onClick={() => onToggleEdit()}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_REJECT}`}
                height='30'
                width='30'
                alt={t('button.cancel')}
              />
              <h1>{t('button.cancel')}</h1>
            </div>
          )
        }
        {(permissions.edit && !showEditLeft) && (
          <div
            className='documentPanelActions-container-btns'
            onClick={async () => {
              if (isEditing) {
                const ok = await (typeof onRequestSave === 'function' ? onRequestSave() : Promise.resolve(false))
                if (ok && typeof onToggleEdit === 'function') { onToggleEdit() }
              } else {
                if (typeof onToggleEdit === 'function') { onToggleEdit() }
              }
            }}
          >
            <input
              type='image'
              src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${isEditing ? process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SAVE : process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_EDIT}`}
              height='30'
              width='30'
              alt={isEditing ? t('button.save') : t('documentPanelActions.edit')}
            />
            <h1>{isEditing ? t('button.save') : t('documentPanelActions.edit')}</h1>
          </div>
        )}
        {fromDelete && permisos && permisos.map((accion, index) => (
          <div
            key={index}
            className='documentPanelActions-container-btns'
            onClick={(e) => onClickBtnActions && onClickBtnActions(e, accion)}
          >
            <input
              type='image'
              src={accion.icon}
              height='30'
              width='30'
              alt={accion.name}
            />
            <h1>{accion.name}</h1>
          </div>
        ))}
        {
          !isInBinPath && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleReturn(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_BACK}`}
                height='30'
                width='30'
                alt={t('button.return')}
              />
              <h1>{t('button.return')}</h1>
            </div>
          )
        }
      </div>

      {showDialogSendEmail && (
        <DialogFormHLPure
          cancel={() => handlers.handleCloseDialog()}
          titleDialog={`${t('documentPanelActions.title.dialog')} ${documentData.name}`}
          descriptionActive={false}
          descriptionDialog={t('documentPanelActions.dialog.description')}
          textBtnCancel={t('button.cancel')}
          saveBtn={t('button.send')}
          handleSubmit={(e) => handlers.handleSubmitDialog(e)}
        >
          <div className='documentPanelActions-dialog-container'>
            <InputHLPure
              typeInput='text'
              required
              labelInput={t('documentPanelActions.dialog.emails')}
              valueInput={email}
              handleChangeInput={(e) => handlers.handleChangeEmail(e)}
              tooltipShow={false}
              informationIcon={false}
              inputActive={true}
              errorInput={errorInput}
              errorInputText={errorInputText}
            />
            <div className='documentPanelActions-dialog-message'>
              <TextAreaHLPure
                required
                labelTextArea={t('documentPanelActions.dialog.message')}
                valueTextData={messageEmail}
                handleChangeTextArea={(e) => handlers.handleChangeMessageEmail(e)}
                errorInput={errorInputArea}
                errorTextArea={errorTextArea}
              />
            </div>
          </div>
        </DialogFormHLPure>
      )}
      {showDialogVersion && (
        <DialogPdfHLPure
          title={t('documentPanelActions.dialog.version')}
          description={documentData.name}
          saveBtn={t('upload.btn')}
          canelBtn={t('button.cancel')}
          width={widthContainer}
          height={heightContainer}
          heightBody={heightBody}
          handleFile={handlers.handleFile}
          handleDeleteFile={handlers.handleDeleteFile}
          cancel={() => handlers.handleCloseDialogVersion()}
          handleSubmitProfile={(e) =>
            handlers.handleVersionDoc(
              e,
              setShowDialogVersion,
              documentData.id,
              fileExpSelected.id
            )
          }
        >
          <Grid container lg={12} md={12} sm={12} es={12}>
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              es={12}
              className='documentPanelActions-dialog-version-btns'
            >
              <InputFileHLPure
                messageBtn={t('documentPanelActions.dialog.version.btn.upload')}
                flex={true}
                acceptFile={'application/pdf'}
                selectedFile={selectedFile}
                selectedFileName={selectedFileName}
                errorFile={errorFile}
                handleFile={(e) => handlers.handleFile(e)}
                handleDeleteFile={(e) => handlers.handleDeleteFile(e)}
              />
            </Grid>

            {/*View document*/}
            {selectedFileBase64 && (
              <div className='documentPanelActions-container-pdf'>
                <div className='documentPanelActions-version-pdf'>{pdf}</div>
                <div className='documentPanelActions-version-pdf-mobile'>
                  <DialogPdf blobUrl={blobUrl} />
                </div>
              </div>
            )}
          </Grid>
        </DialogPdfHLPure>
      )}
      {showDialogShare && (
        <DialogFormHLPure
          titleDialog={`${t('documentPanelActions.dialog.share')} ${documentData.name}`}
          saveBtn={t('documentPanelActions.share')}
          textBtnCancel={t('button.cancel')}
          cancel={() => handlers.handleOpenCloseDialogShare()}
          handleSubmit={(e) => handlers.handleSubmitDialogShare(e)}
        >
          <div className='documentPanelActions-dialog-share-container'>
            <div className='documentPanelActions-search'>
              <SearchBarHLPure
                placeholder={t('digitalVault.editDocument.placeholder.search')}
                onSearchChange={(e) => handlers.filterChangeUsers(e)}
                searchValue={userSearch}
              />
            </div>
            <div className='documentPanelActions-grid-users'>
              <Grid item className='documentPanelActions-grid-documents'>
                <GridSelectionHLPure
                  styleGeneral={true}
                  styleGeneralClass={'documentPanelActions-general-grid'}
                  pageable={false}
                  skip={0}
                  take={usersGrid.length}
                  dataGrid={usersGrid}
                  filter={true}
                  onSelectedItem={userSendGridSelected}
                  columnTwo={false}
                  columnThree={false}
                  columnFive={false}
                  cellColumnOne={(props) => {
                    return (
                      <td className='documentPanelActions-cell-one'>
                        <div className='documentPanelActions-div-cell-one'>
                          <CheckBoxHLPure
                            valueCheckBox={props.dataItem}
                            checkedState={props.dataItem.checked}
                            onChangeHandler={(event) =>
                              handlers.onChangeCheckShare(event, props.dataItem)
                            }
                          />
                          <div className='documentPanelActions-div-user'>
                            <span className='gridHLPure-userName'>
                              {props.dataItem.name}
                            </span>
                            <span className='gridHLPure-fieldSubtitle'>
                              {props.dataItem.email}
                            </span>
                          </div>
                        </div>
                      </td>
                    )
                  }}
                  styleGrid={{ height: '230px' }}
                />
              </Grid>
            </div>
            <div className='documentPanelActions-div-actions-btns'>
              <h1>{t('documentPanelActions.permissions.share')}</h1>
              <div className='documentPanelActions-actions-btns'>
                {permisos.map((permiso, i) => {
                  return (
                    <ButtonActionsHL
                      nameBtn={permiso.name}
                      showIcon={true}
                      icon={permiso.icon}
                      onClickBtn={(e) => handlers.onClickBtnActions(e, permiso)}
                      selected={permiso.checked}
                      key={i}
                    />
                  )
                })}
              </div>
              <div className='documentPanelActions-dialog-message'>
                <TextAreaHLPure
                  labelTextArea={t('documentPanelActions.message.share')}
                  valueTextData={messageShare}
                  handleChangeTextArea={(e) => handlers.handleChangeMessageShare(e)}
                />
              </div>
            </div>
          </div>
        </DialogFormHLPure>
      )}
      {showDeleteDocument && (
        <DialogSystemGLPure
          showConfirm={true}
          confirmTitle={t('documentPanelActions.title.dialog.delete')}
          confirmMessage={t('documentPanelActions.question.dialog.delete', { infoType: infoTypeModal })}
          labelCancelConfirmation={t('button.cancel')}
          labelSubmitConfirmation={t('documentPanelActions.question.dialog.delete.btn')}
          handleConfirmDeleteFlowClose={() => handlers.handleCloseDialogDelete()}
          handleConfirmDeleteFlowSubmit={() => handlers.handleDeleteDocument()}
        />
      )}
      {showDialogPrint && (
        <DialogFormHLPure
          titleDialog={`Documento a imprimir: ${documentData.name}`}
          cancel={() => handlers.handleCloseDialogPrint()}
        >
          <div className='documentPanelActions-print-dialog'>
            <div className='documentPanelActions-print-dialog-embed'>
              <embed
                id='fileDoc'
                type='application/pdf'
                width='450'
                height='400'
              />
            </div>
            <div className='documentPanelActions-print-dialog-btn-cancel'>
              <ButtonHLPure
                nameBtn={'Cerrar'}
                onClickBtn={() => handlers.handleCloseDialogPrint()}
              />
            </div>
          </div>
        </DialogFormHLPure>
      )}
    </div>
  )
}

export default DocumentPanelView